import type { PaymentOptionDetail, PaymentOptionsRecord, UserGroupPayerRecord } from '@navio/nsdk';
import { PaymentOption } from '@navio/nsdk';
import {
  formatPhoneNumber,
  getHighestPriorityPaymentOption,
  isPhoneNumberLandline,
  isPhoneNumberValid
} from 'noddi-util';
import type { OrganizationCarBooking } from './organizationCarBooking';

/** Shared phone validity display for stationary summary + proposal checkout. */
export function deriveStationaryPhoneValidity(
  phone: string | null | undefined,
  options?: { treatInvalidFlag?: boolean }
) {
  const raw = phone ?? '';
  const isLandline = isPhoneNumberLandline(raw);
  const hasValidUserPhone = isPhoneNumberValid(raw) && !isLandline && !options?.treatInvalidFlag;
  return {
    userPhone: raw,
    hasValidUserPhone,
    isLandline,
    invalidUserPhoneDisplay: formatPhoneNumber(raw) || raw
  };
}

/** A proposal's contact, rather than the signed-in member, owns its phone gate. */
export function deriveProposalContactPhoneState(args: {
  proposalPhone: string | null | undefined;
  proposalInvalidPhone: boolean | undefined;
  proposalUserId: number | undefined;
  signedInUserId: number | undefined;
}) {
  const { hasValidUserPhone, ...phoneState } = deriveStationaryPhoneValidity(args.proposalPhone, {
    treatInvalidFlag: args.proposalInvalidPhone
  });
  return {
    ...phoneState,
    hasValidContactPhone: hasValidUserPhone,
    canUpdateContactPhone: args.proposalUserId != null && args.proposalUserId === args.signedInUserId
  };
}

export type StationaryPaymentChoice = {
  defaultPaymentOption: PaymentOption;
  invoiceSelectable: boolean;
  payBeforeSelectable: boolean;
};

function paymentOptionDetail(
  paymentOptions: PaymentOptionDetail[] | undefined,
  option: PaymentOption
): PaymentOptionDetail | undefined {
  return paymentOptions?.find((detail) => detail.option === option);
}

/**
 * Invoice is selectable when the customer profile has it enabled and the
 * service organization still offers it. Pay-before stays selectable whenever
 * the service organization has not disabled it.
 */
export function deriveStationaryPaymentChoice(args: {
  isOrganization: boolean;
  paymentOptions?: PaymentOptionDetail[] | null;
  /** Company-car invoice is an extra choice. Keep the customer's own default. */
  keepPayBeforeDefault?: boolean;
}): StationaryPaymentChoice {
  const paymentOptions = args.paymentOptions ?? undefined;
  if (!paymentOptions) {
    return {
      defaultPaymentOption: PaymentOption.PAY_BEFORE,
      invoiceSelectable: false,
      payBeforeSelectable: false
    };
  }

  const invoice = paymentOptionDetail(paymentOptions, PaymentOption.INVOICE);
  const payBefore = paymentOptionDetail(paymentOptions, PaymentOption.PAY_BEFORE);
  const invoiceSelectable = !!invoice && invoice.enabled && !invoice.disabledByServiceOrganization;
  const payBeforeSelectable = !!payBefore && !payBefore.disabledByServiceOrganization;

  return {
    defaultPaymentOption: defaultPaymentOption({
      paymentOptions,
      isOrganization: args.isOrganization,
      keepPayBeforeDefault: !!args.keepPayBeforeDefault,
      invoiceSelectable,
      payBeforeSelectable
    }),
    invoiceSelectable,
    payBeforeSelectable
  };
}

function defaultPaymentOption(args: {
  paymentOptions: PaymentOptionDetail[];
  isOrganization: boolean;
  keepPayBeforeDefault: boolean;
  invoiceSelectable: boolean;
  payBeforeSelectable: boolean;
}): PaymentOption {
  if (args.keepPayBeforeDefault && args.payBeforeSelectable) {
    return PaymentOption.PAY_BEFORE;
  }
  if (args.invoiceSelectable) {
    return PaymentOption.INVOICE;
  }
  if (args.isOrganization) {
    return getHighestPriorityPaymentOption(args.paymentOptions);
  }
  return PaymentOption.PAY_BEFORE;
}

type StationaryBookingPartyTerms = {
  /** User group the booking is created on. */
  userGroupId: number | null;
  paymentOptions: PaymentOptionDetail[] | undefined;
  /** Separate payer to name on an invoice booking. */
  invoiceTo: UserGroupPayerRecord | null;
};

/**
 * Who a stationary booking is made on, and the payment terms it books under.
 * `member`: the customer's own group, or an organization of theirs they picked.
 * `nonMember`: the customer chose to invoice the organization that owns the
 * car. Pay now stays on the customer. The organization is invoiced itself, so
 * it never names a separate payer.
 */
type StationaryBookingParty = StationaryBookingPartyTerms &
  (
    | { kind: 'member'; isOrganization: boolean; organizationName: string | null }
    | {
        kind: 'nonMember';
        isOrganization: true;
        organizationName: string;
        /** The organization requires a reference on its invoices. */
        invoiceReferenceRequired: boolean;
      }
  );

/** The customer's own organization a booking is made as. */
export type MemberOrganization = { name: string };

/**
 * `organizationCar` is already null when the customer books as an
 * organization of their own, so that organization wins. The membership checks
 * (data quality, existing bookings) stay on `memberUserGroupId` either way.
 */
export function deriveStationaryBookingParty(args: {
  memberUserGroupId: number | null;
  memberOrganization: MemberOrganization | null;
  organizationCar: OrganizationCarBooking | null;
  paymentOptions: Pick<PaymentOptionsRecord, 'paymentOptions' | 'invoiceTo'> | undefined;
  /** True when the customer is paying that company's invoice. */
  bookOnOrganizationCar: boolean;
}): StationaryBookingParty {
  const { memberUserGroupId, memberOrganization, organizationCar, paymentOptions } = args;
  if (organizationCar && args.bookOnOrganizationCar) {
    return {
      kind: 'nonMember',
      userGroupId: organizationCar.userGroupId,
      isOrganization: true,
      organizationName: organizationCar.organizationName,
      invoiceReferenceRequired: organizationCar.invoiceReferenceRequired,
      paymentOptions: organizationCar.paymentOptions,
      invoiceTo: null
    };
  }
  return {
    kind: 'member',
    userGroupId: memberUserGroupId,
    isOrganization: memberOrganization != null,
    organizationName: memberOrganization?.name ?? null,
    paymentOptions: paymentOptions?.paymentOptions,
    invoiceTo: paymentOptions?.invoiceTo ?? null
  };
}

/**
 * Payer to send with an invoice booking. Payment options already drop a payer
 * that is the customer, deleted, or disagreed across the cars, so a set id is
 * the recipient to use.
 */
export function checkoutInvoiceToUserGroupId(
  isInvoice: boolean,
  invoiceTo: { id: number } | null | undefined
): number | null {
  if (!isInvoice || invoiceTo == null) {
    return null;
  }
  return invoiceTo.id;
}

/**
 * Who the invoice banners and the invoice choice name. A company car names its
 * owner even before invoice is chosen, so the choice can say who is invoiced.
 * An empty name falls through to the next one.
 */
export function checkoutInvoiceRecipientName(args: {
  party: { invoiceTo: { name: string } | null; organizationName: string | null };
  organizationCar: Pick<OrganizationCarBooking, 'organizationName'> | null;
  fallbacks: Array<string | null | undefined>;
}): string {
  const { party, organizationCar, fallbacks } = args;
  const fallback = fallbacks.find((name) => name != null && name !== '');
  return organizationCar?.organizationName || party.invoiceTo?.name || party.organizationName || fallback || '';
}

/**
 * A failed invoice create is retried from the booking builder, which does not
 * own the checkout form. Replay the payment method, recipient, booking group,
 * invoice reference, and assigned user from the mutation that failed.
 */
export function checkoutRetryFromMutationVariables(variables: unknown): {
  paymentMethod: PaymentOption;
  userGroupPayerId: number | null;
  userGroupId: number | null;
  invoiceReference: string | null;
  userId: number | null;
} {
  if (variables == null || typeof variables !== 'object') {
    return {
      paymentMethod: PaymentOption.PAY_AFTER,
      userGroupPayerId: null,
      userGroupId: null,
      invoiceReference: null,
      userId: null
    };
  }
  const body = variables as {
    payment?: { paymentMethod?: PaymentOption; invoiceReference?: string } | null;
    userGroupPayerId?: number | null;
    userGroupId?: number | null;
    userId?: number | null;
  };
  return {
    paymentMethod: body.payment?.paymentMethod ?? PaymentOption.PAY_AFTER,
    userGroupPayerId: body.userGroupPayerId ?? null,
    userGroupId: body.userGroupId ?? null,
    invoiceReference: body.payment?.invoiceReference ?? null,
    userId: body.userId ?? null
  };
}

/** Keep a manual pick only while that method is still offered. */
export function resolveStationaryPaymentOption(
  override: PaymentOption | null,
  choice: StationaryPaymentChoice
): PaymentOption {
  if (override === PaymentOption.INVOICE && choice.invoiceSelectable) {
    return PaymentOption.INVOICE;
  }
  if (override === PaymentOption.PAY_BEFORE && choice.payBeforeSelectable) {
    return PaymentOption.PAY_BEFORE;
  }
  return choice.defaultPaymentOption;
}

/**
 * Notice for a car whose owning organization does not include the customer.
 * `bookedOnOrganization`: the booking goes to that organization anyway, named
 * so the customer sees who is invoiced. `owner` / `invoice`: they book as
 * themselves, unnamed because any signed-in customer can enter any plate.
 */
export type NonMemberOwnerNotice =
  | { kind: 'bookedOnOrganization'; organizationName: string }
  | { kind: 'owner' }
  | { kind: 'invoice' };

/**
 * `invoice` only when the owning organization pays by invoice here and invoice
 * is neither selected nor offered as a choice: otherwise the customer still has
 * an invoice of their own.
 */
export function deriveNonMemberOwnerNotice(args: {
  isLoggedIn: boolean;
  bookingParty: StationaryBookingParty;
  paymentOptions: Pick<PaymentOptionsRecord, 'ownerRequiresMembership' | 'invoiceRequiresMembership'> | undefined;
  isInvoice: boolean;
  showPaymentChoice: boolean;
}): NonMemberOwnerNotice | null {
  if (args.bookingParty.kind === 'nonMember') {
    return { kind: 'bookedOnOrganization', organizationName: args.bookingParty.organizationName };
  }
  if (!args.isLoggedIn || !args.paymentOptions?.ownerRequiresMembership) {
    return null;
  }
  const hasOwnInvoice = args.isInvoice || args.showPaymentChoice;
  return { kind: args.paymentOptions.invoiceRequiresMembership && !hasOwnInvoice ? 'invoice' : 'owner' };
}
